import React from 'react';
import { Box, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { descriptionMarkdownComponents } from './EntityDetailDrawer';
import {
    BINDS_VIA_LABEL, browseHref, conceptHref, conceptTypeLabel, formatValue, parameterHref,
    policyHref,
} from './Ontology/ontologyFormat';
import { dsVar, ink } from '../theme/tokens';

export interface ColumnHeader {
    key: string;
    label?: string;
    allow_display?: boolean;
    allowDisplay?: boolean;
}

export interface RelatedActivityTemplate {
    activity_template_code: string;
    activity_template_name?: string;
}

export interface ActivityConcept {
    preferred_label?: string;
    concept_type?: string;
    concept_code?: string;
}

export interface ActivityPolicy {
    title?: string;
    narrative?: string;
    policy_code?: string;
}

/** One resolved threshold from the context's `parameters.in_force`. */
export interface ActivityParameter {
    parameter_code?: string;
    /** How this parameter reached the value it governs — see BINDS_VIA_LABEL. */
    binds_via?: string;
    resolved?: {
        value?: unknown;
        unit?: string;
        concept_code?: string;
        policy_code?: string;
        scope_level?: string;
    };
    /** The candidates this value beat. Its length is what makes the winner credible. */
    supersedes?: unknown[];
}

/**
 * Response of /manage/ontology/activities/<code>/context. The list fields are
 * typed `unknown` on purpose — the API does not reliably return arrays here, so
 * they must go through `toItemArray` before use rather than being trusted.
 */
export interface ActivityContext {
    concepts?: unknown;
    policies?: unknown;
    /**
     * `{ in_force: ActivityParameter[], pending_draft_count: number }`. Newer than the
     * endpoint itself, so an older control plane omits it entirely — every consumer must
     * treat it as optional rather than assume the key is there.
     */
    parameters?: {
        in_force?: unknown;
        pending_draft_count?: number;
    };
}

export interface TaskConfigurationItem {
    key: string;
    label?: string;
    type?: string;
    value?: unknown;
    active?: boolean;
    is_required?: boolean;
    column_headers?: ColumnHeader[];
    columnHeaders?: ColumnHeader[];
}

/**
 * Detail sections for the agent drawer, shown once "View details" has loaded the
 * full activity template:
 *
 *   AgentConfigurations   — `configuration.task_configuration`: each item's label,
 *                           plus its configured value presented as the default.
 *   AgentRelatedTemplates — `related_activity_templates`: the related template names.
 *
 * The snake_case / camelCase drift and the `active: false` soft-delete convention
 * mirror TaskConfigurationForm.jsx, which is the editable counterpart of this view.
 * The helpers are re-declared here rather than imported so this stays a light
 * dependency-free component.
 */
const isActive = (x: { active?: boolean } | null | undefined) => x?.active !== false;
const getColumnHeaders = (item: TaskConfigurationItem): ColumnHeader[] =>
    item?.column_headers ?? item?.columnHeaders ?? [];
const isColumnVisible = (c: ColumnHeader) =>
    c?.key !== 'active' && (c?.allow_display ?? c?.allowDisplay ?? true) !== false;
const cellToString = (v: unknown) => (v === null || v === undefined ? '' : String(v));

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Normalises a payload list field to an array before rendering.
 *
 * These fields are not always the arrays their names suggest. The ontology
 * context returns `concepts` as an object keyed by concept code —
 * `{ "P1": { preferred_label, ... } }` — while `policies` is a real array. A
 * `.length === 0` guard does not catch the object form (an object's `length` is
 * `undefined`), so `.map` threw and took the whole drawer down.
 *
 * Objects are flattened one level, covering both a map of entries and a map of
 * arrays of entries. Anything else degrades to an empty section and warns.
 */
export const toItemArray = <T,>(value: unknown, fieldName: string): T[] => {
    if (Array.isArray(value)) return value as T[];
    if (value === null || value === undefined) return [];

    // A keyed map is a supported shape, not an anomaly — do not warn.
    if (isPlainObject(value)) {
        return Object.keys(value).reduce<T[]>((acc, key) => {
            const entry = value[key];
            if (Array.isArray(entry)) {
                return acc.concat(entry.filter(isPlainObject) as T[]);
            }
            if (isPlainObject(entry)) {
                acc.push(entry as T);
            }
            return acc;
        }, []);
    }

    console.warn(`Expected an array or object for "${fieldName}", received:`, value);
    return [];
};

/**
 * Renders a MULTICOLUMN row as "<first column value> = <second column value>",
 * e.g. `min_segments = 3`. Column order comes from `column_headers` when the
 * template supplies them, otherwise from the row's own key order.
 */
const formatRow = (row: Record<string, unknown>, headers: ColumnHeader[]): string => {
    const keys = headers.length > 0
        ? headers.filter(isColumnVisible).map(h => h.key)
        : Object.keys(row).filter(k => k !== 'active');

    if (keys.length >= 2) {
        return `${cellToString(row[keys[0]])} = ${cellToString(row[keys[1]])}`;
    }
    if (keys.length === 1) {
        return cellToString(row[keys[0]]);
    }
    return '';
};

/**
 * Formats an item's configured value for display. Returns null when there is no
 * usable default (unset, empty string, or empty list), so the caller can omit the
 * "Default" line entirely.
 */
export const formatConfigDefault = (item: TaskConfigurationItem): string[] | null => {
    const value = item?.value;

    if (value === null || value === undefined) return null;

    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed ? [trimmed] : null;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return [String(value)];
    }

    if (Array.isArray(value)) {
        const rows = value.filter(entry => isActive(entry as { active?: boolean }));
        if (rows.length === 0) return null;

        // MULTICOLUMN — a list of row objects, one display line each.
        if (rows.every(isPlainObject)) {
            const headers = getColumnHeaders(item);
            const lines = rows
                .map(row => formatRow(row as Record<string, unknown>, headers))
                .filter(Boolean);
            return lines.length > 0 ? lines : null;
        }

        // MULTITEXT — a list of primitives (or {name} chips), joined on one line.
        const parts = rows
            .map(entry => (isPlainObject(entry) ? cellToString(entry.name) : cellToString(entry)))
            .filter(Boolean);
        return parts.length > 0 ? [parts.join(', ')] : null;
    }

    return [JSON.stringify(value)];
};

/**
 * A link into the Ontology screens, degrading to plain text when there is nothing to
 * link to (an entry with no code, or a code containing a slash, which is not addressable
 * as a single path segment).
 *
 * A plain anchor rather than useNavigate, deliberately: the drawer is docked, so
 * cmd-clicking should open the concept in a new tab and leave the drawer where it is.
 * An onClick handler would swallow that.
 */
const OntologyLink: React.FC<{
    href: string | null;
    label: string;
    bold?: boolean;
}> = ({ href, label, bold }) => {
    const baseSx = {
        fontSize: '0.875rem',
        lineHeight: 1.6,
        fontWeight: bold ? 700 : 400,
    };

    if (!href) {
        return (
            <Typography component="span" sx={{ ...baseSx, color: bold ? dsVar('fg-body') : dsVar('fg-soft') }}>
                {label}
            </Typography>
        );
    }

    return (
        <Box
            component="a"
            href={href}
            sx={{
                ...baseSx,
                color: dsVar('accent-fg'),
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
            }}
        >
            {label}
        </Box>
    );
};

/** Section chrome shared by the drawer's detail sections. */
const DrawerSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${dsVar('line')}` }}>
        <Typography
            component="div"
            sx={{ fontSize: '0.9rem', fontWeight: 600, color: dsVar('fg-body'), mb: 1.5 }}
        >
            {title}
        </Typography>
        {children}
    </Box>
);

/**
 * Related templates declared by the activity template. Renders nothing when the
 * list is absent or empty — unlike Configurations, this section is supplementary.
 *
 * When `onSelect` is supplied each entry becomes a link that navigates the drawer
 * to that template; `disabled` suppresses selection while a fetch is in flight.
 */
export const AgentRelatedTemplates: React.FC<{
    items: unknown;
    onSelect?: (item: RelatedActivityTemplate) => void;
    disabled?: boolean;
}> = ({ items: rawItems, onSelect, disabled }) => {
    const items = toItemArray<RelatedActivityTemplate>(rawItems, 'related_activity_templates');
    if (items.length === 0) return null;

    if (!onSelect) {
        return (
            <DrawerSection title="Related agents">
                <Box component="ul" sx={{ m: 0, pl: 2.5, listStyleType: 'disc' }}>
                    {items.map(item => (
                        <Typography
                            component="li"
                            key={item.activity_template_code}
                            sx={{ fontSize: '0.875rem', color: dsVar('fg-soft'), lineHeight: 1.6, mb: 0.5 }}
                        >
                            {item.activity_template_name || item.activity_template_code}
                        </Typography>
                    ))}
                </Box>
            </DrawerSection>
        );
    }

    return (
        <DrawerSection title="Related agents">
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {items.map(item => (
                    <Box component="li" key={item.activity_template_code} sx={{ m: 0 }}>
                        <Box
                            component="button"
                            type="button"
                            onClick={() => onSelect(item)}
                            disabled={disabled}
                            sx={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                py: 0.75,
                                px: 0,
                                textAlign: 'left',
                                border: 'none',
                                backgroundColor: 'transparent',
                                color: dsVar('accent-fg'),
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '0.875rem',
                                lineHeight: 1.6,
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                                '&:disabled': { opacity: 0.5, cursor: 'not-allowed', textDecoration: 'none' },
                            }}
                        >
                            <Box component="span">{item.activity_template_name || item.activity_template_code}</Box>
                            <ChevronRightIcon sx={{ fontSize: 18, flexShrink: 0 }} />
                        </Box>
                    </Box>
                ))}
            </Box>
        </DrawerSection>
    );
};

/**
 * Ontology concepts for the activity. Each entry shows its preferred label with
 * the concept type as a muted mono tag. Renders nothing when the list is empty.
 */
export const AgentConcepts: React.FC<{ items: unknown }> = ({ items }) => {
    const concepts = toItemArray<ActivityConcept>(items, 'concepts');
    if (concepts.length === 0) return null;

    return (
        <DrawerSection title="Referenced terms">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {concepts.map((concept, index) => (
                    <Box
                        key={`${concept.concept_code ?? concept.preferred_label ?? 'concept'}-${index}`}
                        sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5 }}
                    >
                        <OntologyLink
                            href={conceptHref(concept.concept_code)}
                            label={concept.preferred_label || concept.concept_code || '—'}
                        />
                        {concept.concept_type && (
                            <Typography
                                component="span"
                                sx={{
                                    flexShrink: 0,
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: '0.625rem',
                                    letterSpacing: '0.04em',
                                    textTransform: 'uppercase',
                                    color: dsVar('fg-muted'),
                                }}
                            >
                                {/* The shared label, not the stored value — this tag sits
                                    beside links into a library that calls it "Red flag". */}
                                {conceptTypeLabel(concept.concept_type)}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>
        </DrawerSection>
    );
};

/**
 * Ontology policies for the activity: a title with its narrative rendered as
 * markdown, using the same token styles as the drawer description.
 */
export const AgentPolicies: React.FC<{ items: unknown }> = ({ items }) => {
    const policies = toItemArray<ActivityPolicy>(items, 'policies');
    if (policies.length === 0) return null;

    return (
        <DrawerSection title="Applicable policies">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {policies.map((policy, index) => (
                    <Box key={`${policy.policy_code ?? policy.title ?? 'policy'}-${index}`}>
                        {(policy.title || policy.policy_code) && (
                            <Box sx={{ mb: 0.5 }}>
                                <OntologyLink
                                    href={policyHref(policy.policy_code)}
                                    label={policy.title || policy.policy_code || ''}
                                    bold
                                />
                            </Box>
                        )}
                        {policy.narrative && (
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={descriptionMarkdownComponents}>
                                {policy.narrative}
                            </ReactMarkdown>
                        )}
                    </Box>
                ))}
            </Box>
        </DrawerSection>
    );
};

/**
 * The thresholds this agent will actually apply, and what is still unreviewed.
 *
 * "In force" is a stronger claim than "configured", and the difference is the point of
 * this section. These are the winners of the same precedence chain the engine runs —
 * organisation over regulatory over platform, then most-specific scope — so the number
 * shown here is the number a detection node will use. `supersedes` records what each one
 * beat, which is what makes that claim checkable rather than asserted.
 *
 * The pending-draft count is separate because drafts are invisible to the in-force query
 * by design: the engine must never resolve a draft. Without surfacing it, a reviewer has
 * no way to know from this screen that live thresholds here have no approver.
 */
export const AgentParameters: React.FC<{
    items: unknown;
    pendingDraftCount?: number;
    activityCode?: string;
}> = ({ items, pendingDraftCount = 0, activityCode }) => {
    const parameters = toItemArray<ActivityParameter>(items, 'parameters.in_force');
    if (parameters.length === 0 && pendingDraftCount === 0) return null;

    return (
        <DrawerSection title="Thresholds in force">
            {parameters.length === 0 ? (
                <Typography component="p" sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), m: 0 }}>
                    Nothing approved is in force for this agent yet.
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {parameters.map((parameter, index) => {
                        const resolved = parameter.resolved ?? {};
                        const beaten = Array.isArray(parameter.supersedes)
                            ? parameter.supersedes.length : 0;

                        return (
                            <Box key={`${parameter.parameter_code ?? 'parameter'}-${index}`}>
                                <Box sx={{
                                    display: 'flex', alignItems: 'baseline',
                                    justifyContent: 'space-between', gap: 1.5,
                                }}>
                                    <OntologyLink
                                        href={parameterHref(parameter.parameter_code)}
                                        label={parameter.parameter_code || '—'}
                                    />
                                    <Typography
                                        component="span"
                                        sx={{
                                            flexShrink: 0, fontSize: '0.875rem',
                                            fontWeight: 600, color: dsVar('fg-body'),
                                        }}
                                    >
                                        {formatValue(resolved.value, resolved.unit)}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.25 }}>
                                    {parameter.binds_via && (
                                        <Typography
                                            component="span"
                                            sx={{
                                                fontFamily: '"JetBrains Mono", monospace',
                                                fontSize: '0.625rem', letterSpacing: '0.04em',
                                                textTransform: 'uppercase', color: dsVar('fg-muted'),
                                            }}
                                        >
                                            {BINDS_VIA_LABEL[parameter.binds_via] ?? parameter.binds_via}
                                        </Typography>
                                    )}
                                    {beaten > 0 && (
                                        <Typography
                                            component="span"
                                            sx={{ fontSize: '0.625rem', color: dsVar('fg-muted'), letterSpacing: '0.04em' }}
                                        >
                                            BEAT {beaten} OTHER{beaten === 1 ? '' : 'S'}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {pendingDraftCount > 0 && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${dsVar('line')}` }}>
                    <Box
                        component="a"
                        href={browseHref('parameters', {
                            activity: activityCode,
                            status: 'draft',
                        })}
                        sx={{
                            fontSize: '0.8125rem', color: ink('#FBBF24'), textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        {pendingDraftCount} proposed threshold{pendingDraftCount === 1 ? '' : 's'}
                        {' '}awaiting review
                    </Box>
                    <Typography component="p" sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), m: 0, mt: 0.25 }}>
                        Drafts are not applied by any node until they are approved.
                    </Typography>
                </Box>
            )}
        </DrawerSection>
    );
};

const AgentConfigurations: React.FC<{ items: unknown }> = ({ items }) => {
    const visibleItems = toItemArray<TaskConfigurationItem>(items, 'task_configuration').filter(isActive);

    return (
        <DrawerSection title="Configurations">
            {visibleItems.length === 0 ? (
                <Typography component="p" sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), m: 0 }}>
                    No configurations.
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {visibleItems.map(item => {
                        const defaultLines = formatConfigDefault(item);
                        return (
                            <Box key={item.key}>
                                <Typography
                                    component="p"
                                    sx={{ fontSize: '0.875rem', color: dsVar('fg-soft'), lineHeight: 1.6, m: 0 }}
                                >
                                    {item.label || item.key}
                                </Typography>
                                {defaultLines && (
                                    <Typography
                                        component="p"
                                        sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), lineHeight: 1.6, m: 0, mt: 0.25 }}
                                    >
                                        <Typography component="span" sx={{ color: dsVar('fg-muted'), fontSize: '0.75rem' }}>
                                            Default:{' '}
                                        </Typography>
                                        {defaultLines.length === 1 ? (
                                            defaultLines[0]
                                        ) : (
                                            <Box component="span" sx={{ display: 'block', pl: 1 }}>
                                                {defaultLines.map((line, index) => (
                                                    <Box component="span" key={index} sx={{ display: 'block' }}>
                                                        {line}
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}
        </DrawerSection>
    );
};

export default AgentConfigurations;
